import './CashOperations.scss';

import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';

import {
    NavLink,
    useLocation,
    useNavigate
} from 'react-router-dom';

import {
    TabPanel,
    TabView
} from 'primereact/tabview';

import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import DataTable, {
    FILTER_TYPES,
    DataTableContext,
    DataTableProvider
} from '../../component/datatable2';

import InputSwitch from '../../component/input/InputSwitch';
import CashReconciliation from './CashReconciliation';
import CashOperationSidebar from './CashOperationSidebar';

import { MessageContext } from '../../context/MessageContext';

import { LocationService } from '../../service/LocationService';
import { DeviceService } from '../../service/DeviceService';
import { RouteService } from '../../service/RouteService';
import { TransactionService } from '../../service/TransactionService';

import useNavigationContext from '../../utils/navigationcontext/NavigationContext';
import { RenderedHelper } from '../../utils/render/RenderedHelper';
import { FormattedUtils } from '../../utils/FormattedUtils';
import { Constants } from '../../utils/Constants';

import {
    ReactComponent as DepositIcon
} from '../../assets/images/add-circle-white.svg';

import {
    ReactComponent as WithdrawalIcon
} from '../../assets/images/minus-cirlce.svg';

import {
    ReactComponent as CashCountIcon
} from '../../assets/images/set_balance.svg';

const TYPE_OF_OPERATION_OPTIONS = [
    {
        id: 'SALE',
        labelKey: 'TransactionType.SALE'
    },
    {
        id: 'WITHDRAWAL',
        labelKey: 'TransactionType.WITHDRAWAL'
    },
    {
        id: 'DEPOSIT',
        labelKey: 'TransactionType.DEPOSIT'
    },
    {
        id: 'SET_BALANCE',
        labelKey: 'TransactionType.SET_BALANCE'
    }
];

const CASH_HISTORY_TAB_ROUTE_BY_INDEX = {
    0: '/cash-operations',
    1: '/cash-operations/reconciliations'
};

const CASH_HISTORY_TAB_INDEX_BY_ROUTE = {
    '/cash-operations': 0,
    '/cash-operations/reconciliations': 1
};

const isModifiedLinkClick = (event) => {
    return (
        event.button !== 0 ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        event.altKey
    );
};

const CashOperationsHistoryContent = () => {
    const { t } = useTranslation();

    const tableRef = useRef(null);
    const isMounted = useRef(false);

    const {
        showErrorMessage
    } = useContext(MessageContext);

    const [
        locationOptions,
        setLocationOptions
    ] = useState([]);

    const [
        deviceOptions,
        setDeviceOptions
    ] = useState([]);

    const [
        specialistOptions,
        setSpecialistOptions
    ] = useState([]);

    const [
        sidebarState,
        setSidebarState
    ] = useState({
        visible: false,
        operationType: null
    });

    const [
        operationsHistorySwitch,
        setOperationsHistorySwitch
    ] = useState(false);

    const typeOfOperationOptions = useMemo(
        () => TYPE_OF_OPERATION_OPTIONS.map(
            (option) => ({
                id: option.id,
                label: t(option.labelKey)
            })
        ),
        [t]
    );

    const enums = useMemo(
        () => [
            {
                name: 'TransactionType',
                values: typeOfOperationOptions.map(
                    (option) => ({
                        key: option.id,
                        value: option.label
                    })
                )
            }
        ],
        [typeOfOperationOptions]
    );

    const fetchDeviceOptions = useCallback(
        async () => {
            try {
                const devices =
                    await DeviceService
                        .getDevicesForCashOperations();

                if (!isMounted.current) {
                    return;
                }

                setDeviceOptions(
                    devices.map((device) => ({
                        id: device.id,
                        label: device.name
                    }))
                );
            } catch (error) {
                if (isMounted.current) {
                    showErrorMessage(
                        t(
                            'reports.sales.error.fetchDeviceOptions'
                        )
                    );
                }
            }
        },
        [
            showErrorMessage,
            t
        ]
    );

    const fetchLocationOptions = useCallback(
        async () => {
            try {
                const locations =
                    await LocationService
                        .getAllLocations();

                if (!isMounted.current) {
                    return;
                }

                setLocationOptions(
                    locations.map((location) => ({
                        id: location.id,
                        label: location.name
                    }))
                );
            } catch (error) {
                if (isMounted.current) {
                    showErrorMessage(
                        t(
                            'reports.sales.error.fetchLocationOptions'
                        )
                    );
                }
            }
        },
        [
            showErrorMessage,
            t
        ]
    );

    const fetchSpecialistOptions = useCallback(
        async () => {
            try {
                const technicians =
                    await RouteService
                        .getTechniciansList();

                if (!isMounted.current) {
                    return;
                }

                setSpecialistOptions(
                    technicians.map(
                        (technician) => ({
                            id: technician.id,
                            label: technician.name
                        })
                    )
                );
            } catch (error) {
                if (isMounted.current) {
                    showErrorMessage(
                        t(
                            'cashoperations.error.fetchSpecialistOptions'
                        )
                    );
                }
            }
        },
        [
            showErrorMessage,
            t
        ]
    );

    useEffect(() => {
        isMounted.current = true;

        fetchDeviceOptions();
        fetchLocationOptions();
        fetchSpecialistOptions();

        return () => {
            isMounted.current = false;
        };
    }, [
        fetchDeviceOptions,
        fetchLocationOptions,
        fetchSpecialistOptions
    ]);

    const filterConfig = useMemo(
        () => [
            {
                fieldName: 'operationId',
                label: t(
                    'cashoperations.operationId'
                ),
                type: FILTER_TYPES.TEXT,
                apiParamName: 'operation_id'
            },
            {
                fieldName: 'dateTime',
                label: t(
                    'cashoperations.dateTime'
                ),
                type: FILTER_TYPES.DATE_TIME_RANGE,
                apiParamName:
                    'transaction_date_time',
                apiValueFromConverter: (value) =>
                    moment(value)
                        .startOf('day')
                        .utc()
                        .format(
                            Constants
                                .DATE_TIME_FORMAT_GET
                        ),
                apiValueToConverter: (value) =>
                    moment(value)
                        .endOf('day')
                        .utc()
                        .format(
                            Constants
                                .DATE_TIME_FORMAT_GET
                        )
            },
            {
                fieldName: 'typeOfOperation',
                label: t(
                    'cashoperations.typeOfOperation'
                ),
                type: FILTER_TYPES.LIST,
                apiParamName: 'transaction_type',
                params: typeOfOperationOptions,
                apiValueConverter: (value) =>
                    value
                        .map((item) =>
                            item.id.toUpperCase()
                        )
                        .join(',')
            },
            {
                fieldName: 'locationName',
                label: t(
                    'cashoperations.location'
                ),
                type: FILTER_TYPES.LIST,
                apiParamName: 'location_ids',
                params: locationOptions,
                apiValueConverter: (value) =>
                    value
                        .map((item) => item.id)
                        .join(',')
            },
            {
                fieldName: 'deviceName',
                label: t(
                    'cashoperations.device'
                ),
                type: FILTER_TYPES.LIST,
                apiParamName: 'device_ids',
                params: deviceOptions,
                apiValueConverter: (value) =>
                    value
                        .map((item) => item.id)
                        .join(',')
            },
            {
                fieldName: 'specialistName',
                label: t(
                    'cashoperations.technician'
                ),
                type: FILTER_TYPES.LIST,
                apiParamName: 'specialist_ids',
                params: specialistOptions,
                apiValueConverter: (value) =>
                    value
                        .map((item) => item.id)
                        .join(',')
            },
            {
                fieldName: 'routeId',
                label: t(
                    'cashoperations.routeId'
                ),
                type: FILTER_TYPES.TEXT,
                apiParamName: 'route_id'
            },
            {
                fieldName: 'bagId',
                label: t(
                    'cashoperations.bagId'
                ),
                type: FILTER_TYPES.TEXT,
                apiParamName: 'bag_id'
            },
            {
                fieldName: 'transactionAmount',
                label: t(
                    'cashoperations.transactionAmount'
                ),
                type: FILTER_TYPES.AMOUNT,
                apiParamName: 'transaction_amount',
                params: {
                    maxValue: 999999,
                    minValue: 0
                }
            },
            {
                fieldName: 'balance',
                label: t(
                    'cashoperations.balance'
                ),
                type: FILTER_TYPES.AMOUNT,
                apiParamName: 'balance',
                params: {
                    maxValue: 999999,
                    minValue: 0
                }
            }
        ],
        [
            t,
            typeOfOperationOptions,
            locationOptions,
            deviceOptions,
            specialistOptions
        ]
    );

    const fetchData = useCallback(
        async (criteria) => {
            try {
                return await TransactionService
                    .getCashTransactions(
                        criteria,
                        operationsHistorySwitch,
                        enums
                    );
            } catch (error) {
                console.error(
                    'CashOperationsHistory fetchData error',
                    error
                );

                if (isMounted.current) {
                    showErrorMessage(
                        t(
                            'cashoperations.error.fetch'
                        )
                    );
                }

                return {
                    data: [],
                    totalRecords: 0
                };
            }
        },
        [
            operationsHistorySwitch,
            enums,
            showErrorMessage,
            t
        ]
    );

    const closeSidebar = useCallback(() => {
        setSidebarState({
            visible: false,
            operationType: null
        });
    }, []);

    const openSidebar = useCallback(
        (operationType) => {
            setSidebarState({
                visible: true,
                operationType
            });
        },
        []
    );

    const tableToolbarRight = (
        <div className="flex gap-2 align-items-center">
            <InputSwitch
                label={t(
                    'cashoperations.showOnlyLastOperation'
                )}
                checked={operationsHistorySwitch}
                onChange={(event) => {
                    setOperationsHistorySwitch(
                        Boolean(event.value)
                    );

                    tableRef.current?.refresh();
                }}
            />

            <Button
                className="small"
                onClick={() => {
                    openSidebar('cashDeposit');
                }}
            >
                <DepositIcon />

                <span className="p-button-label pl-2">
                    {t(
                        'cashoperations.cashOperationSidebar.titleDeposit'
                    )}
                </span>
            </Button>

            <Button
                className="small"
                onClick={() => {
                    openSidebar('cashWithdrawal');
                }}
            >
                <WithdrawalIcon />

                <span className="p-button-label pl-2">
                    {t(
                        'cashoperations.cashOperationSidebar.titleWithdrawal'
                    )}
                </span>
            </Button>

            <Button
                className="small"
                onClick={() => {
                    openSidebar('cashCount');
                }}
            >
                <CashCountIcon />

                <span className="p-button-label pl-2">
                    {t(
                        'cashoperations.cashOperationSidebar.titleCashCount'
                    )}
                </span>
            </Button>
        </div>
    );

    return (
        <>
            <CashOperationSidebar
                visible={sidebarState.visible}
                onHide={closeSidebar}
                onSuccess={() => {
                    closeSidebar();
                    tableRef.current?.refresh();
                }}
                viewMode="NEW"
                operationType={
                    sidebarState.operationType
                }
            />

            <DataTable
                ref={tableRef}
                fetchData={fetchData}
                filterConfig={filterConfig}
                defaultSearchCriteria={{
                    first: 0,
                    rows: 25,
                    sortField: 'dateTime',
                    sortOrder: -1,
                    baseQuery: '',
                    filters: {}
                }}
                tableToolbarRight={
                    tableToolbarRight
                }
            >
                <Column
                    field="dateTime"
                    header={t(
                        'cashoperations.dateTime'
                    )}
                    sortable
                    sortField="dateTime"
                    id="dateTime"
                    headerClassName="dt2-header-wrap"
                    body={(row) =>
                        RenderedHelper
                            .formatTimestamp(
                                row.dateTime
                            )
                    }
                    style={{
                        minWidth: '130px',
                        maxWidth: '150px'
                    }}
                />

                <Column
                    field="typeOfOperation"
                    header={t(
                        'cashoperations.typeOfOperation'
                    )}
                    sortable
                    sortField="typeOfOperation"
                    id="typeOfOperation"
                    headerClassName="dt2-header-wrap"
                    body={(row) =>
                        RenderedHelper.formatEnum(
                            row.typeOfOperation,
                            'TransactionType'
                        )
                    }
                    style={{
                        minWidth: '130px',
                        maxWidth: '150px'
                    }}
                />

                <Column
                    field="locationName"
                    header={t(
                        'cashoperations.location'
                    )}
                    sortable
                    sortField="locationName"
                    id="locationName"
                    style={{
                        minWidth: '140px',
                        maxWidth: '180px'
                    }}
                />

                <Column
                    field="deviceName"
                    header={t(
                        'cashoperations.device'
                    )}
                    sortable
                    sortField="deviceName"
                    id="deviceName"
                    style={{
                        minWidth: '140px',
                        maxWidth: '180px'
                    }}
                />

                <Column
                    field="specialistName"
                    header={t(
                        'cashoperations.technician'
                    )}
                    sortable
                    sortField="specialistName"
                    id="specialistName"
                    headerClassName="dt2-header-wrap"
                    style={{
                        minWidth: '130px',
                        maxWidth: '160px'
                    }}
                />

                <Column
                    field="routeId"
                    header={t(
                        'cashoperations.routeId'
                    )}
                    sortable
                    sortField="routeId"
                    id="routeId"
                    headerClassName="dt2-header-wrap"
                    style={{
                        minWidth: '100px',
                        maxWidth: '120px'
                    }}
                />

                <Column
                    field="bagId"
                    header={t(
                        'cashoperations.bagId'
                    )}
                    sortable
                    sortField="bagId"
                    id="bagId"
                    headerClassName="dt2-header-wrap"
                    style={{
                        minWidth: '100px',
                        maxWidth: '120px'
                    }}
                />

                <Column
                    field="transactionAmount"
                    header={t(
                        'cashoperations.transactionAmount'
                    )}
                    sortable
                    sortField="transactionAmount"
                    id="transactionAmount"
                    headerClassName="dt2-header-wrap"
                    body={(row) =>
                        RenderedHelper
                            .renderTransactionAmount(
                                row.transactionAmount,
                                row.typeOfOperation,
                                row.currency
                            )
                    }
                    style={{
                        minWidth: '130px',
                        maxWidth: '150px'
                    }}
                />

                <Column
                    field="balance"
                    header={t(
                        'cashoperations.balance'
                    )}
                    sortable
                    sortField="balance"
                    id="balance"
                    body={(row) =>
                        FormattedUtils.formatPrice(
                            row.balance,
                            row.currency
                        )
                    }
                    style={{
                        minWidth: '110px',
                        maxWidth: '130px'
                    }}
                />
            </DataTable>
        </>
    );
};

const CashOperations = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    /*
     * The URL is the only source of truth for the selected tab.
     */
    const activeTabIndex =
        CASH_HISTORY_TAB_INDEX_BY_ROUTE[
            location.pathname
        ] ?? 0;

    const defaultOperationsHistorySearchCriteria =
        useMemo(
            () => ({
                first: 0,
                rows: 25,
                sortField: 'dateTime',
                sortOrder: -1,
                baseQuery: '',
                filters: {}
            }),
            []
        );

    const defaultReconciliationSearchCriteria =
        useMemo(
            () => ({
                first: 0,
                rows: 25,
                sortField: 'dateTime',
                sortOrder: -1,
                baseQuery: '',
                filters: {}
            }),
            []
        );

    const [
        operationsHistorySearchCriteria,
        setOperationsHistorySearchCriteria
    ] = useState(
        () => ({
            ...defaultOperationsHistorySearchCriteria
        })
    );

    const [
        reconciliationSearchCriteria,
        setReconciliationSearchCriteria
    ] = useState(
        () => ({
            ...defaultReconciliationSearchCriteria
        })
    );

    const navigationContext = useNavigationContext(
        (params) => {
            setOperationsHistorySearchCriteria(
                params?.operationsHistorySearchCriteria
                    ? params.operationsHistorySearchCriteria
                    : defaultOperationsHistorySearchCriteria
            );

            setReconciliationSearchCriteria(
                params?.reconciliationSearchCriteria
                    ? params.reconciliationSearchCriteria
                    : defaultReconciliationSearchCriteria
            );
        }
    );

    const navigationContextRef =
        useRef(navigationContext);

    useEffect(() => {
        navigationContextRef.current =
            navigationContext;
    }, [navigationContext]);

    /*
     * Store table criteria only. The active tab is represented by
     * the URL and must not be restored from navigation context.
     */
    useEffect(() => {
        navigationContextRef.current.putParam(
            'operationsHistorySearchCriteria',
            operationsHistorySearchCriteria
        );

        navigationContextRef.current.putParam(
            'reconciliationSearchCriteria',
            reconciliationSearchCriteria
        );
    }, [
        operationsHistorySearchCriteria,
        reconciliationSearchCriteria
    ]);

    const handleCashHistoryTabChange =
        useCallback(
            (index) => {
                const route =
                    CASH_HISTORY_TAB_ROUTE_BY_INDEX[
                        index
                    ];

                if (
                    !route ||
                    route === location.pathname
                ) {
                    return;
                }

                navigate({
                    pathname: route,
                    search: location.search
                });
            },
            [
                location.pathname,
                location.search,
                navigate
            ]
        );

    const renderCashHistoryTabHeader =
        useCallback(
            (
                index,
                label
            ) => {
                const route =
                    CASH_HISTORY_TAB_ROUTE_BY_INDEX[
                        index
                    ];

                return (options) => (
                    <NavLink
                        to={{
                            pathname: route,
                            search: location.search
                        }}
                        role="tab"
                        aria-selected={
                            options.selected
                        }
                        aria-controls={
                            options.ariaControls
                        }
                        tabIndex={
                            options.selected ? 0 : -1
                        }
                        className={
                            options.className
                        }
                        onClick={(event) => {
                            /*
                             * Preserve native browser actions:
                             *
                             * - Ctrl/Cmd + click
                             * - Shift + click
                             * - middle mouse click
                             * - browser context menu
                             */
                            if (
                                isModifiedLinkClick(
                                    event
                                )
                            ) {
                                return;
                            }

                            event.preventDefault();

                            handleCashHistoryTabChange(
                                index
                            );
                        }}
                    >
                        {label}
                    </NavLink>
                );
            },
            [
                handleCashHistoryTabChange,
                location.search
            ]
        );

    return (
        <div
            className={
                `cash-operations-container ` +
                `cash-operations-container--tab-` +
                activeTabIndex
            }
        >
            <TabView
                className="w-full"
                activeIndex={activeTabIndex}
                onTabChange={(event) => {
                    handleCashHistoryTabChange(
                        event.index
                    );
                }}
                renderActiveOnly
            >
                <TabPanel
                    header={t(
                        'cashoperations.tab.operationsHistory'
                    )}
                    headerTemplate={
                        renderCashHistoryTabHeader(
                            0,
                            t(
                                'cashoperations.tab.operationsHistory'
                            )
                        )
                    }
                >
                    <div className="data-card">
                        <DataTableProvider>
                            <CashOperationsHistoryContent />
                        </DataTableProvider>
                    </div>
                </TabPanel>

                <TabPanel
                    header={t(
                        'cashoperations.tab.cashReconcilations'
                    )}
                    headerTemplate={
                        renderCashHistoryTabHeader(
                            1,
                            t(
                                'cashoperations.tab.cashReconcilations'
                            )
                        )
                    }
                >
                    <div className="data-card">
                        <DataTableProvider>
                            <CashReconciliation />
                        </DataTableProvider>
                    </div>
                </TabPanel>
            </TabView>
        </div>
    );
};

export default CashOperations;